/**
 * Media helper utilities for event media handling
 */

const API_URL = import.meta.env.VITE_API_URL || ''

/**
 * Detect media type from URL or filename
 * @param {string} url - URL or filename
 * @returns {'image' | 'video' | 'pdf'} - Media type
 */
export const detectMediaType = (url) => {
  if (!url) return 'image'
  const lowerUrl = url.toLowerCase()
  
  // Check for YouTube URLs
  if (
    lowerUrl.includes('youtube.com') ||
    lowerUrl.includes('youtu.be')
  ) {
    return 'video'
  }
  
  // Check for Vimeo URLs
  if (lowerUrl.includes('vimeo.com')) {
    return 'video'
  }
  
  // Check file extensions
  if (lowerUrl.match(/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i)) {
    return 'image'
  }
  if (lowerUrl.match(/\.(mp4|webm|mov|avi|mkv)(\?|$)/i)) {
    return 'video'
  }
  if (lowerUrl.match(/\.pdf(\?|$)/i)) {
    return 'pdf'
  }
  
  // Default to image for unknown URLs
  return 'image'
}

/**
 * Check if URL is a YouTube video
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isYouTubeUrl = (url) => {
  if (!url) return false
  return url.includes('youtube.com') || url.includes('youtu.be')
}

/**
 * Check if URL is a Vimeo video
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isVimeoUrl = (url) => {
  if (!url) return false
  return url.includes('vimeo.com')
}

/**
 * Check if URL is an external video (YouTube or Vimeo)
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isExternalVideoUrl = (url) => {
  return isYouTubeUrl(url) || isVimeoUrl(url)
}

/**
 * Extract YouTube video ID from URL
 * @param {string} url - YouTube URL
 * @returns {string|null} - Video ID or null
 */
export const getYouTubeVideoId = (url) => {
  if (!url) return null
  
  // Handle youtu.be short URLs
  const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/)
  if (shortMatch) return shortMatch[1]
  
  // Handle youtube.com URLs
  const longMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/)
  if (longMatch) return longMatch[1]
  
  // Handle youtube.com/embed/ URLs
  const embedMatch = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/)
  if (embedMatch) return embedMatch[1]
  
  return null
}

/**
 * Extract Vimeo video ID from URL
 * @param {string} url - Vimeo URL
 * @returns {string|null} - Video ID or null
 */
export const getVimeoVideoId = (url) => {
  if (!url) return null
  
  const match = url.match(/vimeo\.com\/(\d+)/)
  return match ? match[1] : null
}

/**
 * Get YouTube embed URL
 * @param {string} url - YouTube URL
 * @returns {string|null} - Embed URL or null
 */
export const getYouTubeEmbedUrl = (url) => {
  const videoId = getYouTubeVideoId(url)
  if (!videoId) return null
  return `https://www.youtube.com/embed/${videoId}`
}

/**
 * Get Vimeo embed URL
 * @param {string} url - Vimeo URL
 * @returns {string|null} - Embed URL or null
 */
export const getVimeoEmbedUrl = (url) => {
  const videoId = getVimeoVideoId(url)
  if (!videoId) return null
  return `https://player.vimeo.com/video/${videoId}`
}

/**
 * Get embed URL for external video (YouTube or Vimeo)
 * @param {string} url - Video URL
 * @returns {string|null} - Embed URL or null
 */
export const getVideoEmbedUrl = (url) => {
  if (isYouTubeUrl(url)) return getYouTubeEmbedUrl(url)
  if (isVimeoUrl(url)) return getVimeoEmbedUrl(url)
  return null
}

/**
 * Get YouTube thumbnail URL
 * @param {string} url - YouTube URL
 * @param {string} quality - Thumbnail quality ('default', 'hqdefault', 'mqdefault', 'sddefault', 'maxresdefault')
 * @returns {string|null} - Thumbnail URL or null
 */
export const getYouTubeThumbnail = (url, quality = 'hqdefault') => {
  const videoId = getYouTubeVideoId(url)
  if (!videoId) return null
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`
}

/**
 * Get full URL for media (handles both local and external URLs)
 * @param {string} url - Media URL
 * @returns {string} - Full URL
 */
export const getMediaUrl = (url) => {
  if (!url) return ''
  
  // If it's already a full URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  
  // If it's a local path, prepend API URL
  return `${API_URL}${url}`
}

/**
 * Get display name for media item
 * @param {Object} media - Media object with url and originalName
 * @returns {string} - Display name
 */
export const getMediaDisplayName = (media) => {
  if (media.originalName) return media.originalName
  if (media.caption) return media.caption
  
  // Extract filename from URL
  const url = media.url || ''
  const parts = url.split('/')
  const filename = parts[parts.length - 1]
  
  // Remove query parameters
  return filename.split('?')[0] || 'Media file'
}

/**
 * Get icon/emoji for media type
 * @param {'image' | 'video' | 'pdf'} type - Media type
 * @returns {string} - Icon/emoji
 */
export const getMediaTypeIcon = (type) => {
  switch (type) {
    case 'video':
      return '🎬'
    case 'pdf':
      return '📄'
    case 'image':
    default:
      return '🖼️'
  }
}

/**
 * Get accepted file types for upload input
 * @returns {string} - Accept string for file input
 */
export const getAcceptedMediaTypes = () => {
  return 'image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime,application/pdf'
}

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Validate media file
 * @param {File} file - File to validate
 * @returns {{ valid: boolean, error?: string }} - Validation result
 */
export const validateMediaFile = (file) => {
  const maxSize = 50 * 1024 * 1024 // 50MB
  const allowedTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'video/mp4',
    'video/webm',
    'video/quicktime',
    'application/pdf'
  ]
  
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type: ${file.type}. Allowed: images (JPEG, PNG, GIF, WebP), videos (MP4, WebM), and PDFs.`
    }
  }
  
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File too large: ${formatFileSize(file.size)}. Maximum size is 50MB.`
    }
  }
  
  return { valid: true }
}

/**
 * Validate media URL
 * @param {string} url - URL to validate
 * @returns {{ valid: boolean, error?: string, type?: string }} - Validation result with detected type
 */
export const validateMediaUrl = (url) => {
  if (!url || !url.trim()) {
    return { valid: false, error: 'URL is required' }
  }
  
  const trimmedUrl = url.trim()
  
  // Basic URL validation
  try {
    new URL(trimmedUrl)
  } catch {
    return { valid: false, error: 'Invalid URL format' }
  }
  
  const type = detectMediaType(trimmedUrl)
  
  return { valid: true, type }
}
